import { type JackpotGameResponse, type JackpotGameMap, GameJackpot } from '@game/types'
import { useQuery, useApi, useGamesJackpotsSubsriber, whenever, useCoreConfig, computed, toValue } from '#imports'
import type { MaybeRefOrGetter } from '#imports'

export const useGamesJackpots = (options: { enabled?: MaybeRefOrGetter<boolean> } = {}) => {
    const { site } = useCoreConfig()
    const api = useApi({ service: 'gameConfiguration' })

    function prepareResponse(jackpotsResponse: JackpotGameResponse[]) {
        const gameJackpotsMap: Record<string, JackpotGameMap> = jackpotsResponse?.reduce((acc: any, cur: JackpotGameResponse) => {
            const jackpot = cur.jackpots.find(x => x.type === GameJackpot.SMALL_JACKPOT)
            const superJackpot = cur.jackpots.find(x => x.type === GameJackpot.SUPER_JACKPOT)
            const megaprize = cur.jackpots.find(x => x.type === GameJackpot.MEGAPRIZE)

            acc[cur.slug] = {
                jackpot: jackpot?.value ?? 0,
                superJackpot: superJackpot?.value ?? 0,
                megaprize: megaprize?.value ?? 0,
            }

            return acc
        }, {})

        return gameJackpotsMap
    }

    // Jackpot updates will be received via websocket messages, ensuring up-to-date jackpot data.
    //
    // The async-data `key` shares this query's data across every caller, and the
    // query cache (getCachedData, default 10s TTL) lets lists that mount together
    // share a single fetch instead of one each. `enabled` is passed straight
    // through so jackpot-capable lists have it truthy at setup, taking the
    // cache-aware `immediate` load instead of a cache-bypassing manual refresh.
    const query = useQuery<ReturnType<typeof prepareResponse> | undefined>(async () => {
        const response = await api<JackpotGameResponse[]>('/gateway/game-configuration/public/jackpots', {
            query: {
                brand: site.brand
            }
        })

        return prepareResponse(response)
    }, {
        key: 'game:games-jackpots',
        lazy: true,
        server: false,
        enabled: () => toValue(options.enabled ?? true),
        deep: true,
    })

    const gamesJackpots = computed(() => query.data.value)

    if (import.meta.client) {
        const { jackpots } = useGamesJackpotsSubsriber()

        whenever(jackpots, (jackpotsSubscribeResult: { [gameSlug: string]: number }) => {
            Object.entries(jackpotsSubscribeResult).forEach(([gameSlug, amount]) => {
                if (query.data.value?.[gameSlug]) {
                    query.data.value[gameSlug].superJackpot = amount
                }
            })
        }, {
            immediate: true,
            deep: true,
        })
    }

    return query.enrich({
        gamesJackpots,
    })
}
